<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-container" @click.stop>
      <!-- Header Premium -->
      <div class="modal-header">
        <div class="header-content">
          <div class="icon-wrapper">
            <i class="fas fa-hand-holding-usd"></i>
          </div>
          <div class="header-text">
            <h3>💰 Créance client</h3>
            <p>Déclaration des montants collectés</p>
          </div>
        </div>
        <button @click="closeModal" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Contenu -->
      <div class="modal-body">
        <!-- Informations de la caisse -->
        <div class="caisse-info-section">
          <h4>📅 Caisse du {{ formatDate(caisse?.date) }}</h4>
          <div class="info-grid">
            <div class="info-item">
              <span class="label">Technicien :</span>
              <span class="value">{{ user?.prenom }} {{ user?.nom }}</span>
            </div>
            <div class="info-item">
              <span class="label">Statut :</span>
              <span class="value">{{ caisse?.statut || 'Ouverte' }}</span>
            </div>
          </div>
        </div>

        <!-- Montants attendus -->
        <div class="montants-attendus-section">
          <h4>💡 Montants Attendus (Calculés)</h4>
          <div class="montants-grid">
            <div class="montant-card expected">
              <div class="montant-icon">💵</div>
              <div class="montant-info">
                <div class="montant-label">Espèces</div>
                <div class="montant-value">{{ formatCurrency(caisse?.montantCashAttendu || 0) }}</div>
              </div>
            </div>
            <div class="montant-card expected">
              <div class="montant-icon">📝</div>
              <div class="montant-info">
                <div class="montant-label">Chèques</div>
                <div class="montant-value">{{ formatCurrency(caisse?.montantChequesAttendu || 0) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Déclaration des montants -->
        <div class="declaration-section">
          <h4>✍️ Déclaration des Montants</h4>
          
          <!-- Espèces -->
          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-money-bill-wave"></i>
              Montant Espèces Déclaré (TND) *
            </label>
            <input 
              v-model.number="form.montantCashDeclare"
              type="number" 
              step="0.01"
              min="0"
              class="form-input"
              placeholder="0.00"
              required
            >
          </div>

          <!-- Chèques -->
          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-file-invoice"></i>
              Montant Chèques Déclaré (TND) *
            </label>
            <input 
              v-model.number="form.montantChequesDeclare"
              type="number" 
              step="0.01"
              min="0"
              class="form-input"
              placeholder="0.00"
              required
            >
          </div>

          <!-- Nombre de chèques -->
          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-hashtag"></i>
              Nombre de Chèques
            </label>
            <input 
              v-model.number="form.nombreCheques"
              type="number" 
              min="0"
              class="form-input"
              placeholder="0"
            >
          </div>

          <!-- Pièces justificatives -->
          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-paperclip"></i>
              Pièces Justificatives
            </label>
            <div class="upload-zone" @click="$refs.fileInput.click()">
              <div v-if="!form.piecesJustificatives || form.piecesJustificatives.length === 0" class="upload-placeholder">
                <i class="fas fa-cloud-upload-alt"></i>
                <p>Cliquez pour ajouter des photos des chèques et justificatifs</p>
                <small>JPG, PNG - Max 5MB par fichier</small>
              </div>
              <div v-else class="upload-preview">
                <div v-for="(file, index) in form.piecesJustificatives" :key="index" class="file-preview">
                  <i class="fas fa-file-image"></i>
                  <span>{{ file.name }}</span>
                  <button @click.stop="removeFile(index)" class="btn-remove-file">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>
            </div>
            <input 
              ref="fileInput"
              type="file" 
              accept="image/*"
              multiple
              @change="handleFileUpload"
              style="display: none"
            >
          </div>

          <!-- Commentaires -->
          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-comment"></i>
              Commentaires / Observations
            </label>
            <textarea 
              v-model="form.commentaires"
              class="form-textarea"
              rows="3"
              placeholder="Ajoutez vos observations sur la caisse du jour..."
            ></textarea>
          </div>
        </div>

        <!-- Écarts calculés -->
        <div v-if="hasEcarts" class="ecarts-section">
          <h4>⚠️ Écarts Détectés</h4>
          <div class="ecarts-grid">
            <div v-if="ecartCash !== 0" class="ecart-card" :class="{ 'surplus': ecartCash > 0, 'deficit': ecartCash < 0 }">
              <div class="ecart-icon">
                <i :class="ecartCash > 0 ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"></i>
              </div>
              <div class="ecart-info">
                <div class="ecart-label">Écart Espèces</div>
                <div class="ecart-value">{{ formatCurrency(Math.abs(ecartCash)) }}</div>
                <div class="ecart-type">{{ ecartCash > 0 ? 'Surplus' : 'Déficit' }}</div>
              </div>
            </div>
            <div v-if="ecartCheques !== 0" class="ecart-card" :class="{ 'surplus': ecartCheques > 0, 'deficit': ecartCheques < 0 }">
              <div class="ecart-icon">
                <i :class="ecartCheques > 0 ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"></i>
              </div>
              <div class="ecart-info">
                <div class="ecart-label">Écart Chèques</div>
                <div class="ecart-value">{{ formatCurrency(Math.abs(ecartCheques)) }}</div>
                <div class="ecart-type">{{ ecartCheques > 0 ? 'Surplus' : 'Déficit' }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Signature -->
        <div class="signature-section">
          <h4>✍️ Signature Technicien</h4>
          <div class="signature-container">
            <canvas 
              ref="signatureCanvas"
              @mousedown="startDrawing"
              @mousemove="draw"
              @mouseup="stopDrawing"
              @touchstart="startDrawing"
              @touchmove="draw"
              @touchend="stopDrawing"
              class="signature-canvas"
              width="400"
              height="150"
            ></canvas>
            <div class="signature-controls">
              <button @click="clearSignature" class="btn-clear">
                <i class="fas fa-eraser"></i>
                Effacer
              </button>
              <div v-if="hasSignature" class="signature-status">
                <i class="fas fa-check-circle"></i>
                Signature capturée
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button @click="closeModal" class="btn-cancel">
          <i class="fas fa-times"></i>
          Annuler
        </button>
        <button 
          @click="submitReddition" 
          :disabled="!canSubmit || loading"
          class="btn-submit"
        >
          <i v-if="loading" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-hand-holding-usd"></i>
          {{ loading ? 'Envoi...' : 'Effectuer la Reddition' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { useStore } from 'vuex'

export default {
  name: 'RemisePhysiqueModal',
  props: {
    caisse: Object
  },
  emits: ['close', 'reddition-completed'],
  setup(props, { emit }) {
    const toast = useToast()
    const store = useStore()
    const loading = ref(false)
    const signatureCanvas = ref(null)
    const isDrawing = ref(false)
    const hasSignature = ref(false)

    const user = computed(() => store.getters['auth/user'])

    const form = ref({
      montantCashDeclare: 0,
      montantChequesDeclare: 0,
      nombreCheques: 0,
      commentaires: '',
      piecesJustificatives: []
    })

    // Calculs des écarts
    const ecartCash = computed(() => {
      return (form.value.montantCashDeclare || 0) - (props.caisse?.montantCashAttendu || 0)
    })

    const ecartCheques = computed(() => {
      return (form.value.montantChequesDeclare || 0) - (props.caisse?.montantChequesAttendu || 0)
    })

    const hasEcarts = computed(() => {
      return ecartCash.value !== 0 || ecartCheques.value !== 0
    })

    const canSubmit = computed(() => {
      return form.value.montantCashDeclare >= 0 && 
             form.value.montantChequesDeclare >= 0 &&
             hasSignature.value
    })

    // Gestion de la signature
    const setupCanvas = () => {
      const canvas = signatureCanvas.value
      if (!canvas) return

      const ctx = canvas.getContext('2d')
      ctx.strokeStyle = '#001BB7'
      ctx.lineWidth = 2
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
    }

    const getEventPos = (e) => {
      const canvas = signatureCanvas.value
      const rect = canvas.getBoundingClientRect()
      const clientX = e.clientX || (e.touches && e.touches[0].clientX)
      const clientY = e.clientY || (e.touches && e.touches[0].clientY)
      
      return {
        x: clientX - rect.left,
        y: clientY - rect.top
      }
    }

    const startDrawing = (e) => {
      e.preventDefault()
      isDrawing.value = true
      const pos = getEventPos(e)
      const ctx = signatureCanvas.value.getContext('2d')
      ctx.beginPath()
      ctx.moveTo(pos.x, pos.y)
    }

    const draw = (e) => {
      if (!isDrawing.value) return
      e.preventDefault()
      
      const pos = getEventPos(e)
      const ctx = signatureCanvas.value.getContext('2d')
      ctx.lineTo(pos.x, pos.y)
      ctx.stroke()
      hasSignature.value = true
    }

    const stopDrawing = (e) => {
      if (!isDrawing.value) return
      e.preventDefault()
      isDrawing.value = false
    }

    const clearSignature = () => {
      const canvas = signatureCanvas.value
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      hasSignature.value = false
    }

    const getSignatureData = () => {
      return signatureCanvas.value.toDataURL()
    }

    // Gestion des fichiers multiples
    const handleFileUpload = (event) => {
      const files = Array.from(event.target.files)
      
      files.forEach(file => {
        // Vérifier la taille (5MB max)
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`Le fichier ${file.name} ne doit pas dépasser 5MB`)
          return
        }

        // Convertir en Base64
        const reader = new FileReader()
        reader.onload = (e) => {
          form.value.piecesJustificatives.push({
            name: file.name,
            data: e.target.result,
            type: file.type
          })
        }
        reader.readAsDataURL(file)
      })
    }

    const removeFile = (index) => {
      form.value.piecesJustificatives.splice(index, 1)
    }

    const submitReddition = async () => {
      if (!canSubmit.value) return

      loading.value = true
      try {
        const token = localStorage.getItem('token')
        const signatureData = getSignatureData()

        const response = await fetch(`/api/finance/caisse/${props.caisse._id}/reddition`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...form.value,
            signatureTechnicien: signatureData,
            piecesJustificatives: form.value.piecesJustificatives
          })
        })

        if (response.ok) {
          const data = await response.json()
          toast.success('✅ Créance client effectuée avec succès')
          emit('reddition-completed', data.caisse)
          closeModal()
        } else {
          const error = await response.json()
          toast.error(error.message || 'Erreur lors de la reddition')
        }
      } catch (error) {
        console.error('Erreur reddition:', error)
        toast.error('Erreur lors de la reddition de caisse')
      } finally {
        loading.value = false
      }
    }

    const closeModal = () => {
      emit('close')
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-TN', {
        style: 'currency',
        currency: 'TND'
      }).format(amount || 0)
    }

    const formatDate = (date) => {
      return new Date(date).toLocaleDateString('fr-FR')
    }

    onMounted(() => {
      setupCanvas()
    })

    return {
      user,
      form,
      loading,
      signatureCanvas,
      hasSignature,
      ecartCash,
      ecartCheques,
      hasEcarts,
      canSubmit,
      startDrawing,
      draw,
      stopDrawing,
      clearSignature,
      handleFileUpload,
      removeFile,
      submitReddition,
      closeModal,
      formatCurrency,
      formatDate
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;
}

.modal-container {
  background: white;
  border-radius: 20px;
  max-width: 700px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
  animation: modalSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Header */
.modal-header {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  padding: 2rem;
  border-radius: 20px 20px 0 0;
  position: relative;
  overflow: hidden;
}

.modal-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  position: relative;
  z-index: 1;
}

.icon-wrapper {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  font-size: 1.8rem;
}

.header-text h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.75rem;
  font-weight: 700;
}

.header-text p {
  margin: 0;
  opacity: 0.9;
}

.close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

/* Body */
.modal-body {
  padding: 2rem;
}

/* Sections */
.caisse-info-section,
.montants-attendus-section,
.declaration-section,
.ecarts-section,
.signature-section {
  background: rgba(248, 250, 252, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(0, 27, 183, 0.1);
}

.caisse-info-section h4,
.montants-attendus-section h4,
.declaration-section h4,
.ecarts-section h4,
.signature-section h4 {
  margin: 0 0 1rem 0;
  color: var(--primary-blue);
  font-weight: 700;
  font-size: 1.1rem;
}

/* Info grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
}

.info-item .label {
  font-weight: 600;
  color: #6c757d;
}

.info-item .value {
  font-weight: 500;
  color: #1a202c;
}

/* Montants grid */
.montants-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.montant-card {
  background: white;
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.montant-card.expected {
  border-color: rgba(16, 185, 129, 0.2);
  background: rgba(16, 185, 129, 0.05);
}

.montant-icon {
  font-size: 1.5rem;
}

.montant-label {
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 600;
}

.montant-value {
  font-size: 1.2rem;
  font-weight: 700;
  color: #10b981;
}

/* Form elements */
.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--primary-blue);
  font-size: 0.9rem;
}

.form-label i {
  margin-right: 0.5rem;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid rgba(0, 27, 183, 0.1);
  border-radius: 12px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: white;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 4px rgba(0, 27, 183, 0.1);
  transform: translateY(-2px);
}

/* Écarts */
.ecarts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.ecart-card {
  background: white;
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  border: 2px solid;
}

.ecart-card.surplus {
  border-color: rgba(16, 185, 129, 0.3);
  background: rgba(16, 185, 129, 0.05);
}

.ecart-card.deficit {
  border-color: rgba(239, 68, 68, 0.3);
  background: rgba(239, 68, 68, 0.05);
}

.ecart-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.surplus .ecart-icon {
  background: rgba(16, 185, 129, 0.2);
  color: #10b981;
}

.deficit .ecart-icon {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.ecart-label {
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 600;
}

.ecart-value {
  font-size: 1.1rem;
  font-weight: 700;
}

.surplus .ecart-value {
  color: #10b981;
}

.deficit .ecart-value {
  color: #ef4444;
}

.ecart-type {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Upload zone */
.upload-zone {
  border: 2px dashed rgba(0, 27, 183, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
}

.upload-zone:hover {
  border-color: var(--primary-blue);
  background: rgba(0, 27, 183, 0.02);
}

.upload-placeholder i {
  font-size: 2.5rem;
  color: var(--primary-blue);
  margin-bottom: 1rem;
}

.upload-preview {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.file-preview {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(0, 27, 183, 0.05);
  padding: 0.75rem;
  border-radius: 8px;
}

.file-preview i {
  color: #10b981;
  font-size: 1.2rem;
}

.file-preview span {
  flex: 1;
  font-size: 0.9rem;
  font-weight: 500;
}

.btn-remove-file {
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  transition: all 0.3s ease;
}

.btn-remove-file:hover {
  background: #dc2626;
  transform: scale(1.1);
}

/* Signature */
.signature-container {
  background: white;
  border-radius: 12px;
  padding: 1rem;
  border: 2px solid rgba(0, 27, 183, 0.1);
}

.signature-canvas {
  width: 100%;
  border: 2px dashed #e2e8f0;
  border-radius: 8px;
  cursor: crosshair;
  background: #fafafa;
}

.signature-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
}

.btn-clear {
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-clear:hover {
  background: #5a6268;
  transform: translateY(-2px);
}

.signature-status {
  color: #10b981;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Footer */
.modal-footer {
  padding: 1.5rem 2rem;
  background: rgba(248, 250, 252, 0.5);
  border-top: 1px solid rgba(0, 27, 183, 0.1);
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  border-radius: 0 0 20px 20px;
}

.btn-cancel,
.btn-submit {
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
}

.btn-cancel {
  background: #6c757d;
  color: white;
}

.btn-cancel:hover {
  background: #5a6268;
  transform: translateY(-2px);
}

.btn-submit {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 27, 183, 0.3);
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-50px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .modal-container {
    width: 95%;
    margin: 1rem;
  }
  
  .modal-header {
    padding: 1.5rem;
  }
  
  .modal-body {
    padding: 1.5rem;
  }
  
  .montants-grid,
  .ecarts-grid {
    grid-template-columns: 1fr;
  }
  
  .signature-canvas {
    height: 120px;
  }
}
</style>
